import { Link } from "react-router-dom";
import { useClientWaiting } from "../hooks/useClientWaiting";
import { waitLabel } from "./notes/ClientWaiting";
import { ChatCircleDots, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";

const KIND = { job: "Job", emergency: "Emergency", project: "Project", "job-request": "Request" };

/* Dashboard card: open items where the client's latest message has gone unanswered for over a day. Hides when empty. */
export default function ClientWaitingCard({ limit = 8 }) {
  const { items } = useClientWaiting();
  if (!items.length) return null;
  const shown = items.slice(0, limit);
  return (
    <div className="bg-card border border-amber-300/60 dark:border-amber-800/50 rounded-lg overflow-hidden" data-testid="client-waiting-card">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-amber-50/60 dark:bg-amber-950/20">
        <ChatCircleDots size={16} weight="duotone" className="text-amber-600 dark:text-amber-400" />
        <h3 className="text-sm font-semibold">Clients waiting on a reply</h3>
        <span className="ml-1 rounded-full bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid="client-waiting-count">{items.length}</span>
        <span className="ml-auto text-[10px] text-muted-foreground hidden sm:inline">Client messages unanswered for over a day</span>
      </div>
      <ul className="divide-y divide-border">
        {shown.map((w) => (
          <li key={`${w.etype}-${w.id}`}>
            <Link to={`${w.path}${w.etype === "job" || w.etype === "job-request" ? "?tab=notes" : w.etype === "project" ? "?view=notes" : ""}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 transition-colors group" data-testid={`client-waiting-row-${w.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono text-primary">{w.reference}</span>
                  <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{KIND[w.etype]}</span>
                  <span className="truncate">{w.title}</span>
                </p>
                <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                  <span className="font-medium text-foreground/80">{w.author}</span>{w.client ? ` · ${w.client}` : ""}{w.snippet ? ` — “${w.snippet}”` : ""}
                </p>
              </div>
              <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1 shrink-0" title={w.read ? `Read by ${w.read_by.join(", ")}` : "Unread by the team"}>
                {w.read ? <Eye size={11} /> : <EyeSlash size={11} className="text-amber-600" />}
              </span>
              <span className="rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap shrink-0">{waitLabel(w.hours)}</span>
              <ArrowRight size={14} className="text-muted-foreground group-hover:text-primary shrink-0" />
            </Link>
          </li>
        ))}
      </ul>
      {items.length > limit && <p className="px-4 py-2 text-[11px] text-muted-foreground border-t border-border">+ {items.length - limit} more</p>}
    </div>
  );
}
